import { useCallback, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { ExportLoader } from '@/components/GlobalLoader/ExportLoader';
import GlobalLoader from '@/components/GlobalLoader/GlobalLoader';
import ModalManager from '@/components/Modal/ModalManager';
import PopupManager from '@/components/Popup/PopupManager';
import ToastManager from '@/components/Toast/ToastManager';
import AMP_KEYS from '@/configs/amp-track-keys';
import DetailModalManager from '@/features/Commons/DetailModal/DetailModalManager';
import Sidebar from '@/features/Commons/Sidebar';
import { useSidebarWidth } from '@/hooks/domain/useSidebar';
import useCommonStore from '@/stores/pure/CommonStore';
import useUIStore from '@/stores/pure/UIStore';
import { track } from '@/utils/track/track';

import { PublicProvider } from '../../contexts/PublicContext';

const AppLayout = () => {
  const location = useLocation();

  const sidebarHidden = useUIStore((state) => state.sidebarHidden);
  const globalLoading = useUIStore((state) => state.globalLoading);

  const sidebarWidth = useSidebarWidth();

  const { setAppPathHistory, appPathHistory } = useCommonStore((state) => ({
    setAppPathHistory: state.setAppPathHistory,
    appPathHistory: state.appPathHistory,
  }));

  const { current: currentPath } = appPathHistory;

  useEffect(() => {
    track(AMP_KEYS.PAGE_VIEW);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!currentPath) {
      setAppPathHistory('current', globalThis.location.pathname);
      return;
    }

    // Set only when new path is different from current path
    if (currentPath === globalThis.location.pathname) return;
    setAppPathHistory('prev', currentPath);
    setAppPathHistory('current', globalThis.location.pathname);
  }, [location.pathname, currentPath, setAppPathHistory]);

  const storePathValues = useCallback(() => {
    const storage = globalThis?.sessionStorage;
    if (!storage) return;

    const prevPath = storage.getItem('currentPath');
    const currentPath = globalThis.location.pathname;

    if (prevPath === currentPath) return;

    storage.setItem('prevPath', prevPath || '');
    storage.setItem('currentPath', globalThis.location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    storePathValues();
  }, [storePathValues]);

  return (
    <PublicProvider isPublic={false}>
      <Style sidebarWidth={sidebarWidth}>
        {!sidebarHidden && <Sidebar />}
        <ToastManager />
        <PopupManager />
        <DetailModalManager />
        <ModalManager />
        <GlobalLoader
          loading={globalLoading.status}
          message={globalLoading.message}
        />
        <ExportLoader />
        <div className="body-area">
          <Outlet />
        </div>
      </Style>
    </PublicProvider>
  );
};

const Style = styled.div<{ sidebarWidth: number }>`
  height: 100vh;
  width: 100%;
  & > .body-area {
    height: 100%;
    width: calc(100vw - ${(props) => props.sidebarWidth}px);
    margin-left: ${(props) => props.sidebarWidth}px;
    background-color: ${(props) => props.theme.colors.background};
  }
`;

export default AppLayout;
